/**
 * WorkStatusTable Component
 * 라이더 근무 현황 테이블 (협력사별 아코디언 형식)
 */
import React, { useState } from 'react';
import { SupplierWorkStatus, RiderWorkStatusItem } from '../types/riderWorkStatus.types';
import SortIcon from '@/shared/components/dataDisplay/SortIcon';

type SortKey = 'riderId' | 'riderName' | 'totalWorkDays';
type SortDirection = 'asc' | 'desc' | null;

interface WorkStatusTableProps {
    data: SupplierWorkStatus[];
    loading: boolean;
    minWorkDays?: number;
}

const WorkStatusTable: React.FC<WorkStatusTableProps> = ({ data, loading, minWorkDays }) => {
    const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>(null);

    /**
     * 협력사 아코디언 토글
     */
    const toggleSupplier = (suppId: string) => {
        setExpandedSuppliers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(suppId)) {
                newSet.delete(suppId);
            } else {
                newSet.add(suppId);
            }
            return newSet;
        });
    };

    /**
     * 전체 펼치기
     */
    const expandAll = () => {
        const allSuppIds = new Set(data.map(supplier => supplier.suppId));
        setExpandedSuppliers(allSuppIds);
    };

    /**
     * 전체 접기
     */
    const collapseAll = () => {
        setExpandedSuppliers(new Set());
    };

    /**
     * 전체 펼침 여부 확인
     */
    const isAllExpanded = data.length > 0 && expandedSuppliers.size === data.length;

    /**
     * 날짜 목록 추출 (모든 라이더의 workStatusByDate에서 날짜 수집)
     */
    const getDateList = (supplier: SupplierWorkStatus): string[] => {
        const dateSet = new Set<string>();
        supplier.riders.forEach((rider: RiderWorkStatusItem) => {
            Object.keys(rider.workStatusByDate).forEach(date => {
                dateSet.add(date);
            });
        });
        return Array.from(dateSet).sort();
    };

    /**
     * 날짜 포맷팅 (YYYY-MM-DD -> MM/DD)
     */
    const formatDate = (dateStr: string): string => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[1]}/${parts[2]}`;
        }
        return dateStr;
    };

    /**
     * 정렬 핸들러
     */
    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortKey(null);
                setSortDirection(null);
            }
        } else {
            setSortKey(key);
            setSortDirection('asc');
        }
    };

    /**
     * 라이더 목록 필터링 및 정렬
     */
    const getFilteredAndSortedRiders = (riders: RiderWorkStatusItem[]): RiderWorkStatusItem[] => {
        // 1. 필터링
        let filteredRiders = riders;
        if (minWorkDays !== undefined && minWorkDays > 0) {
            filteredRiders = riders.filter(rider => rider.totalWorkDays >= minWorkDays);
        }

        // 2. 정렬
        if (!sortKey || !sortDirection) return filteredRiders;

        return [...filteredRiders].sort((a, b) => {
            let aVal: string | number;
            let bVal: string | number;

            switch (sortKey) {
                case 'riderId':
                    aVal = a.riderId;
                    bVal = b.riderId;
                    break;
                case 'riderName':
                    aVal = a.riderName;
                    bVal = b.riderName;
                    break;
                case 'totalWorkDays':
                    aVal = a.totalWorkDays;
                    bVal = b.totalWorkDays;
                    break;
                default:
                    return 0;
            }

            if (typeof aVal === 'string' && typeof bVal === 'string') {
                return sortDirection === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }

            return sortDirection === 'asc'
                ? (aVal as number) - (bVal as number)
                : (bVal as number) - (aVal as number);
        });
    };

    /**
     * 근무 여부 표시
     */
    const renderWorkStatus = (isWorking: boolean | undefined): React.ReactNode => {
        if (isWorking === undefined) {
            return <span className="work-status-badge no-data">-</span>;
        }
        return (
            <span className={`work-status-badge ${isWorking ? 'working' : 'not-working'}`}>
                {isWorking ? '근무' : '미근무'}
            </span>
        );
    };

    if (loading && data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="empty-state">
                        <p>데이터를 불러오는 중...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="empty-state">
                        <p>조회된 근무 현황이 없습니다.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                {/* 전체 펼치기/접기 버튼 */}
                <div className="accordion-controls">
                    <button
                        className="accordion-toggle-btn"
                        onClick={isAllExpanded ? collapseAll : expandAll}
                    >
                        <i className={`fas fa-${isAllExpanded ? 'compress-alt' : 'expand-alt'}`}></i>
                        {isAllExpanded ? '전체 접기' : '전체 펼치기'}
                    </button>
                </div>
                <div className="work-status-accordion">
                    {data.map((supplier) => {
                        const isExpanded = expandedSuppliers.has(supplier.suppId);
                        const dateList = getDateList(supplier);

                        return (
                            <div key={supplier.suppId} className="supplier-accordion-item">
                                {/* 협력사 헤더 */}
                                <div
                                    className="supplier-header"
                                    onClick={() => toggleSupplier(supplier.suppId)}
                                >
                                    <div className="supplier-header-content">
                                        <span className="supplier-name">{supplier.suppName}</span>
                                        <span className="supplier-info">
                                            ({supplier.suppId}) · 라이더 {supplier.riders.length}명
                                        </span>
                                    </div>
                                    <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                </div>

                                {/* 라이더 목록 (아코디언 내용) */}
                                {isExpanded && (
                                    <div className="supplier-content">
                                        <div className="table-responsive">
                                            <table className="work-status-table">
                                                <thead>
                                                    <tr>
                                                        <th
                                                            className="sortable-header"
                                                            onClick={() => handleSort('riderId')}
                                                        >
                                                            라이더 ID
                                                            <SortIcon direction={sortKey === 'riderId' ? sortDirection : null} />
                                                        </th>
                                                        <th
                                                            className="sortable-header"
                                                            onClick={() => handleSort('riderName')}
                                                        >
                                                            라이더명
                                                            <SortIcon direction={sortKey === 'riderName' ? sortDirection : null} />
                                                        </th>
                                                        <th>배달처리비 합계</th>
                                                        <th
                                                            className="sortable-header"
                                                            onClick={() => handleSort('totalWorkDays')}
                                                        >
                                                            총 근무일수
                                                            <SortIcon direction={sortKey === 'totalWorkDays' ? sortDirection : null} />
                                                        </th>
                                                        {dateList.map((date) => (
                                                            <th key={date}>{formatDate(date)}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getFilteredAndSortedRiders(supplier.riders).map((rider) => (
                                                        <tr key={rider.riderId}>
                                                            <td>{rider.riderId}</td>
                                                            <td>{rider.riderName}</td>
                                                            <td className="delivery-fee-total">
                                                                {rider.deliveryFeeTotal !== undefined
                                                                    ? `${rider.deliveryFeeTotal.toLocaleString()}원`
                                                                    : '-'
                                                                }
                                                            </td>
                                                            <td className="total-work-days">
                                                                {rider.totalWorkDays}일
                                                            </td>
                                                            {dateList.map((date) => (
                                                                <td key={date} className="work-status-cell">
                                                                    {renderWorkStatus(
                                                                        rider.workStatusByDate[date]
                                                                    )}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default WorkStatusTable;
